import {
  AthletePreview,
  AthleteTeamProfile,
  BlockType,
  CRICKET_BOWLING_COACH_PROFILE_ROLE,
} from '@strength/domain';
import { isValidUUID } from '@strength/utils';
import { useRouter, useRouterState, useSearch } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useFlags } from '../../../context/featureFlag';
import { useViewer } from '../../../context/viewer';
import { useStorage } from '../../../hooks/useStorage';
import {
  CalendarSyncData,
  useCalendarSync,
} from '../calendar/hooks/useCalendarSync';
import {
  useCrossTeamData,
  UseCrossTeamDataResult,
} from '../hooks/useCrossTeamData';
import { PlanData, usePlanData } from '../hooks/usePlanData';
import { TimelineSyncData, useTimelineSync } from '../hooks/useTimelineSync';
import { TrackPreview } from '../timeline/types';
import { calculateFirstBlockDate } from '../timeline/utils';

const SHOW_CANCELED_KEY = 'show-canceled';
// v2: the v1 key holds `false` for most existing users — the old
// reset-on-deselect effect wrote it on any visit without a selected athlete —
// which would shadow the new ON default. Bumping the key orphans those stale
// values; manual selections are retained going forward.
const SHOW_ADDITIONAL_TEAMS_KEY = 'show-additional-teams-v2';

// Keyed by team, like TRACK_COLLAPSED_KEY in TimelinePage — the parent ids in
// it are team-scoped, and a coach expects each team to remember its own.
const GROUP_LABEL_COLLAPSED_KEY =
  'annual-planner-collapsed-group-labels-by-team';
// A plain boolean, not per team: it is a view preference like `show-canceled`,
// not a set of team-scoped ids.
const ARCHIVED_SECTION_EXPANDED_KEY = 'archived-section-expanded';

type PlanContextType = PlanData &
  TimelineSyncData &
  CalendarSyncData &
  UseCrossTeamDataResult & {
    teamId: string;
    // tracked using search params not react state
    selectedAthlete: AthletePreview | null;
    setSelectedAthlete: (athleteId: string | null) => void;
    showCanceled: boolean;
    setShowCanceled: (value: boolean) => void;
    showAdditionalTeams: boolean;
    setShowAdditionalTeams: (value: boolean) => void;
    // Timeline date (scroll position) — shared between Timeline and Calendar
    timelineDate: DateTime;
    setTimelineDate: (date: DateTime) => void;
    // Date constraints for timeline and calendar navigation
    minDate: DateTime;
    maxDate: DateTime;
    // Cross-team collapse state — shared between the sidebar
    // (AdditionalTeamsTracks) and the timeline (BlockPanel) so both panels
    // render the same set of rows. Sets track the *exceptions* to the
    // expanded-by-default behavior, matching the current-team `collapsedTracks`
    // pattern: a team or parent track is collapsed only when its id is in the
    // corresponding set.
    collapsedCrossTeamTeamIds: ReadonlySet<string>;
    toggleCrossTeamTeamCollapsed: (teamId: string) => void;
    collapsedCrossTeamTrackIds: ReadonlySet<string>;
    toggleCrossTeamTrackCollapsed: (trackId: string) => void;
    // Archived-tracks section expand/collapse — shared between the sidebar
    // (ArchivedTracksSection) and the timeline (BlockPanel) so both panels
    // build the same shared row list. Persisted; collapsed by default.
    archivedSectionExpanded: boolean;
    toggleArchivedSection: () => void;
    // Parent ids whose Archive-section label is collapsed, persisted per team
    // like `collapsedTracks`. Separate from that set, which belongs to the
    // parent's row in the active list: this toggle only governs the parent's
    // archived sub-tracks.
    collapsedGroupLabelIds: ReadonlySet<string>;
    toggleGroupLabelCollapsed: (parentTrackId: string) => void;
    // Whether the additional-teams (cross-team) section has anything to show —
    // computed once here so TimelinePage and BlockPanel can't derive it
    // differently.
    hasAdditionalTeams: boolean;
    // Calendar print drawer open state (SC-4068) — the trigger button lives
    // in the page header (outside CalendarProvider), but the drawer itself
    // renders inside CalendarPage, so both read/write it via PlanContext.
    printDrawerOpen: boolean;
    setPrintDrawerOpen: (open: boolean) => void;
  };

const PlanContext = createContext<PlanContextType | null>(null);

/*
 * This context is used to store the state and data used by both the Timeline and Calendar Page.
 * We bootstrap the Timeline and Calendar data here and useTimelineSync to determine the date bounds of both the Calendar and Timeline.
 */
export function PlanProvider({ children }: { children: ReactNode }) {
  const {
    team: { selectedTeam: teamId },
    profile: { role },
  } = useViewer();
  const { navigate } = useRouter();
  const {
    location: { pathname },
  } = useRouterState();
  // Using useSearch with strict: false because this component has child routes.
  // When a child route is active, the matched route is the child, not this parent.
  const { athleteId } = useSearch({ strict: false }) as { athleteId?: string };

  const blockTypeFilter: BlockType[] =
    role === CRICKET_BOWLING_COACH_PROFILE_ROLE ? ['bowling'] : [];

  const selectedAthleteId =
    athleteId && isValidUUID(athleteId) ? athleteId : undefined;

  const setSelectedAthlete = useCallback(
    (newAthleteId: string | null) => {
      navigate({
        to: pathname,
        params: { teamId },
        search: newAthleteId ? { athleteId: newAthleteId } : {},
      });
    },
    [navigate, pathname, teamId],
  );

  const [showCanceled, setShowCanceled] = useStorage(SHOW_CANCELED_KEY, false);
  // Defaults ON for flagged orgs; useStorage retains the user's last manual
  // selection across sessions. The toggle is hidden without a selected
  // athlete and every consumer gates on an athlete being present, so the
  // stored value is inert until one is picked — no reset needed.
  const [showAdditionalTeams, setShowAdditionalTeams] = useStorage(
    SHOW_ADDITIONAL_TEAMS_KEY,
    true,
  );

  const [timelineDate, setTimelineDate] = useState<DateTime>(DateTime.now());

  // Cross-team collapse state — see PlanContextType for defaults / semantics.
  const [collapsedCrossTeamTeamIds, setCollapsedCrossTeamTeamIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const [collapsedCrossTeamTrackIds, setCollapsedCrossTeamTrackIds] = useState<
    ReadonlySet<string>
  >(() => new Set());

  const toggleCrossTeamTeamCollapsed = useCallback((teamId: string) => {
    setCollapsedCrossTeamTeamIds((prev) => {
      const next = new Set(prev);
      if (next.has(teamId)) {
        next.delete(teamId);
      } else {
        next.add(teamId);
      }
      return next;
    });
  }, []);

  const toggleCrossTeamTrackCollapsed = useCallback((trackId: string) => {
    setCollapsedCrossTeamTrackIds((prev) => {
      const next = new Set(prev);
      if (next.has(trackId)) {
        next.delete(trackId);
      } else {
        next.add(trackId);
      }
      return next;
    });
  }, []);

  // Collapsed by default; persisted, so a coach who works with the section open
  // finds it open again after a reload.
  const [archivedSectionExpanded, setArchivedSectionExpanded] = useStorage(
    ARCHIVED_SECTION_EXPANDED_KEY,
    false,
  );
  // Persisted per team, like the active tracks' collapse state — a coach who
  // collapses a group expects it to stay collapsed across a reload. A label the
  // coach never touched is absent from the set, and so expanded.
  const [collapsedGroupLabelsByTeam, setCollapsedGroupLabelsByTeam] =
    useStorage<Record<string, string[]>>(GROUP_LABEL_COLLAPSED_KEY, {});
  const collapsedGroupLabelIds = useMemo(
    () => new Set(teamId ? (collapsedGroupLabelsByTeam[teamId] ?? []) : []),
    [teamId, collapsedGroupLabelsByTeam],
  );
  const toggleArchivedSection = useCallback(() => {
    setArchivedSectionExpanded((prev) => !prev);
  }, [setArchivedSectionExpanded]);
  const toggleGroupLabelCollapsed = useCallback(
    (parentTrackId: string) => {
      if (!teamId) return;
      setCollapsedGroupLabelsByTeam((prev) => {
        const current = prev[teamId] ?? [];
        const next = current.includes(parentTrackId)
          ? current.filter((id) => id !== parentTrackId)
          : [...current, parentTrackId];
        return { ...prev, [teamId]: next };
      });
    },
    [teamId, setCollapsedGroupLabelsByTeam],
  );

  const [printDrawerOpen, setPrintDrawerOpen] = useState(false);

  const { showAdditionalTeamsFlag } = useFlags();

  // Fetch plan data (tRPC queries)
  const planData = usePlanData(teamId, selectedAthleteId);
  const { trackIds, filteredTracks, athletes } = planData;
  // Fetch loco-sync data (blocks and focuses)
  const blocksData = useTimelineSync(trackIds, showCanceled, blockTypeFilter);
  const { blocks, athleteTransferredTracks } = blocksData;
  // Fetch loco-sync data (workout events)
  const calendarData = useCalendarSync(
    selectedAthleteId ?? null,
    trackIds,
    showCanceled,
  );

  // Fetch cross-team tracks for the selected athlete when the user has toggled
  // showAdditionalTeams on and the feature flag is enabled
  const crossTeamData = useCrossTeamData({
    athleteProfileId: selectedAthleteId ?? null,
    enabled: showAdditionalTeams && showAdditionalTeamsFlag,
    timelineDate,
    activeTeamId: teamId,
  });

  // Calculate date constraints for timeline and calendar navigation
  const minDate = useMemo(() => {
    return calculateFirstBlockDate(blocks, filteredTracks).startOf('month');
  }, [blocks, filteredTracks]);

  const maxDate = useMemo(() => {
    return DateTime.now().plus({ years: 1 }).endOf('month');
  }, []);

  // Get the selected athlete object for convenience
  const selectedAthlete = useMemo(() => {
    return athletes?.find((a) => a.id === selectedAthleteId) || null;
  }, [athletes, selectedAthleteId]);

  const hasAdditionalTeams = useMemo(
    () => showAdditionalTeams && crossTeamData.additionalTeamsData.length > 0,
    [showAdditionalTeams, crossTeamData.additionalTeamsData],
  );

  const tracksWithTransferItems = useMemo(
    () =>
      filteredTracks.map((ft) => ({
        ...ft,
        athleteTransfer: athleteTransferredTracks?.find(
          (att) => att.trackId === ft.id,
        ),
        multiTeamTransferOptions: getMultiTeamManualTransferOptions(
          ft,
          athletes,
          teamId,
        ),
      })),
    [filteredTracks, athleteTransferredTracks, athletes, teamId],
  );

  const value = useMemo<PlanContextType>(
    () => ({
      teamId,
      selectedAthlete,
      setSelectedAthlete,
      showCanceled,
      setShowCanceled,
      showAdditionalTeams,
      setShowAdditionalTeams,
      timelineDate,
      setTimelineDate,
      minDate,
      maxDate,
      collapsedCrossTeamTeamIds,
      toggleCrossTeamTeamCollapsed,
      collapsedCrossTeamTrackIds,
      toggleCrossTeamTrackCollapsed,
      archivedSectionExpanded,
      toggleArchivedSection,
      collapsedGroupLabelIds,
      toggleGroupLabelCollapsed,
      hasAdditionalTeams,
      printDrawerOpen,
      setPrintDrawerOpen,
      ...planData,
      ...blocksData,
      ...calendarData,
      ...crossTeamData,
      filteredTracks: tracksWithTransferItems,
    }),
    [
      teamId,
      selectedAthlete,
      setSelectedAthlete,
      showCanceled,
      setShowCanceled,
      showAdditionalTeams,
      setShowAdditionalTeams,
      timelineDate,
      setTimelineDate,
      minDate,
      maxDate,
      collapsedCrossTeamTeamIds,
      toggleCrossTeamTeamCollapsed,
      collapsedCrossTeamTrackIds,
      toggleCrossTeamTrackCollapsed,
      archivedSectionExpanded,
      toggleArchivedSection,
      collapsedGroupLabelIds,
      toggleGroupLabelCollapsed,
      hasAdditionalTeams,
      printDrawerOpen,
      setPrintDrawerOpen,
      planData,
      blocksData,
      calendarData,
      crossTeamData,
      tracksWithTransferItems,
    ],
  );

  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
}

export function usePlanContext() {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error('usePlanContext must be used within a PlanProvider');
  }
  return context;
}

/**
 * Multi-team transfer option is only supported for tracks with one athlete.
 * We support manually transferring the track for the athlete from one team to the other.
 */
function getMultiTeamManualTransferOptions(
  track: TrackPreview,
  athletes: AthleteTeamProfile[],
  currentTeamId: string,
) {
  // Only support manual multi-team transfer for tracks with one athlete
  if (!track.athletes || track.athletes.length !== 1) return undefined;

  const trackAthlete = track.athletes[0];
  const trackAthleteProfile = athletes.find(
    (a) => a.id === trackAthlete?.profileId,
  );

  if (!trackAthleteProfile) return undefined;
  // Fewer than two known teams means either a single-team athlete (no other
  // team to transfer to) or destinations not loaded yet (the Zero roster
  // restores them asynchronously). Exposing the option in either case would
  // open a destination-less modal, so it waits until a destination exists.
  if (trackAthleteProfile.teams.length < 2) return undefined;

  return trackAthleteProfile.teams.filter((t) => t.id !== currentTeamId);
}
